import { useState } from 'react';
import {
    Card,
    Title1,
    Title3,
    Body1,
    Input,
    Button,
    Field,
    MessageBar,
    MessageBarBody,
    Spinner,
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogContent,
    DialogActions,
    Link,
} from '@fluentui/react-components';
import { login, User, requestPasswordReset } from '../services/api';
import EDIPartnersLogo from '../components/EDIPartnersLogo';

interface LoginPageProps {
    onLogin: (user: User) => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Forgot password dialog state
    const [forgotDialogOpen, setForgotDialogOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotMessage, setForgotMessage] = useState('');
    const [forgotError, setForgotError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await login(username, password);
            onLogin(response.user);
        } catch (err: any) {
            setError(err.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!forgotEmail) {
            setForgotError('Please enter your email address');
            return;
        }

        setForgotLoading(true);
        setForgotError('');
        setForgotMessage('');

        try {
            const result = await requestPasswordReset(forgotEmail);
            setForgotMessage(result.message);
            // Clear the email field
            setForgotEmail('');
        } catch (err: any) {
            setForgotError(err.message || 'Failed to send reset email');
        } finally {
            setForgotLoading(false);
        }
    };

    const handleForgotDialogClose = () => {
        setForgotDialogOpen(false);
        setForgotEmail('');
        setForgotError('');
        setForgotMessage('');
    };

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                padding: '20px',
                background: 'linear-gradient(135deg, #1a2b4b 0%, #2d4a7c 100%)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Decorative geometric shapes */}
            <div
                style={{
                    position: 'absolute',
                    top: '-10%',
                    right: '-5%',
                    width: '400px',
                    height: '400px',
                    background: 'rgba(0, 180, 216, 0.1)',
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    transform: 'rotate(45deg)',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    bottom: '-15%',
                    left: '-10%',
                    width: '500px',
                    height: '500px',
                    background: 'rgba(0, 180, 216, 0.08)',
                    borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
                    transform: 'rotate(-15deg)',
                }}
            />

            <Card
                style={{
                    width: '100%',
                    maxWidth: '420px',
                    padding: '40px',
                    borderRadius: '12px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                {/* Logo and Header */}
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    {/* EDI Partners Logo */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '16px',
                        }}
                    >
                        <EDIPartnersLogo height={56} showText={true} />
                    </div>
                    <Title1 style={{ marginBottom: '8px', color: '#1a2b4b', display: 'block', textAlign: 'center' }}>
                        Client Upload Portal
                    </Title1>
                    <Body1 style={{ color: '#6c757d', display: 'block', marginTop: '8px', textAlign: 'center' }}>
                        Sign in to upload your EDI files
                    </Body1>
                </div>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                            <MessageBarBody>{error}</MessageBarBody>
                        </MessageBar>
                    )}

                    <Field label="Username" required style={{ marginBottom: '16px' }}>
                        <Input
                            type="text"
                            value={username}
                            onChange={(_e, data) => setUsername(data.value)}
                            placeholder="Enter your username"
                            disabled={loading}
                            autoComplete="username"
                            style={{ borderRadius: '6px' }}
                        />
                    </Field>

                    <Field label="Password" required style={{ marginBottom: '16px' }}>
                        <Input
                            type="password"
                            value={password}
                            onChange={(_e, data) => setPassword(data.value)}
                            placeholder="Enter your password"
                            disabled={loading}
                            autoComplete="current-password"
                            style={{ borderRadius: '6px' }}
                        />
                    </Field>

                    {/* Forgot Password Link */}
                    <div style={{ textAlign: 'right', marginBottom: '24px' }}>
                        <Link
                            style={{ fontSize: '13px', cursor: 'pointer' }}
                            onClick={() => setForgotDialogOpen(true)}
                        >
                            Forgot password?
                        </Link>
                    </div>

                    <Button
                        type="submit"
                        appearance="primary"
                        disabled={loading || !username || !password}
                        style={{
                            width: '100%',
                            padding: '12px',
                            fontSize: '16px',
                            fontWeight: '600',
                            borderRadius: '6px',
                            background: loading ? '#6c757d' : '#28a745',
                            border: 'none',
                        }}
                    >
                        {loading ? <Spinner size="tiny" /> : 'Sign In'}
                    </Button>
                </form>

                <div
                    style={{
                        marginTop: '32px',
                        paddingTop: '24px',
                        borderTop: '1px solid #e9ecef',
                        textAlign: 'center',
                    }}
                >
                    <Body1
                        style={{
                            color: '#6c757d',
                            fontSize: '13px',
                        }}
                    >
                        Need access? Contact your EDI administrator
                    </Body1>
                    <Title3
                        style={{
                            color: '#1a2b4b',
                            marginTop: '8px',
                            fontSize: '14px',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                        }}
                    >
                        804-755-4381
                    </Title3>
                    <Body1
                        style={{
                            color: '#1a2b4b',
                            marginTop: '4px',
                            fontSize: '14px',
                            display: 'block',
                            textAlign: 'center',
                        }}
                    >
                        <a href="mailto:EDISupport@EDI-Partners.com" style={{ color: '#2d4a7c', textDecoration: 'none' }}>
                            EDISupport@EDI-Partners.com
                        </a>
                    </Body1>
                </div>
            </Card>

            {/* Forgot Password Dialog */}
            <Dialog open={forgotDialogOpen} onOpenChange={(_, data) => data.open ? setForgotDialogOpen(true) : handleForgotDialogClose()}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogContent>
                            <Body1 style={{ marginBottom: '16px', display: 'block' }}>
                                Enter your email address and we'll send you a link to reset your password.
                            </Body1>

                            {forgotError && (
                                <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                                    <MessageBarBody>{forgotError}</MessageBarBody>
                                </MessageBar>
                            )}

                            {forgotMessage && (
                                <MessageBar intent="success" style={{ marginBottom: '16px' }}>
                                    <MessageBarBody>{forgotMessage}</MessageBarBody>
                                </MessageBar>
                            )}

                            {!forgotMessage && (
                                <Field label="Email Address" required>
                                    <Input
                                        type="email"
                                        value={forgotEmail}
                                        onChange={(_e, data) => setForgotEmail(data.value)}
                                        placeholder="Enter your email"
                                        disabled={forgotLoading}
                                        style={{ width: '100%' }}
                                    />
                                </Field>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={handleForgotDialogClose}>
                                {forgotMessage ? 'Close' : 'Cancel'}
                            </Button>
                            {!forgotMessage && (
                                <Button
                                    appearance="primary"
                                    onClick={handleForgotPassword}
                                    disabled={forgotLoading || !forgotEmail}
                                >
                                    {forgotLoading ? <Spinner size="tiny" /> : 'Send Reset Link'}
                                </Button>
                            )}
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
}
